/**
 * SnippetExamples.tsx
 * Comprehensive examples of HeroUI Snippet component
 * 
 * Showcases all HeroUI Snippet features using our shared components
 */

import React from 'react';
import { PlaygroundPageLayout } from '../shared';
import { Snippet } from '@enfomentis/ui';
import { Card, CardBody, CardHeader } from '@enfomentis/ui';


export const SnippetExamples: React.FC = () => {
  return (
    <PlaygroundPageLayout>
      <div className="space-y-8">
        
        {/* Basic Usage */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Basic Usage</h3>
          <Card>
            <CardBody>
              <div className="space-y-6">
                <div>
                  <p className="text-text-primary mb-3">Simple command snippets with copy functionality:</p>
                  <div className="space-y-3">
                    <Snippet>npm install @heroui/react</Snippet>
                    <Snippet>yarn add @heroui/react</Snippet>
                    <Snippet>pnpm add @heroui/react</Snippet>
                  </div>
                </div>
                <div className="text-sm text-text-secondary">
                  Click the copy icon to copy the command to your clipboard
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        {/* Size Variations */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Size Variations</h3>
          <Card>
            <CardBody>
              <div className="space-y-6">
                <div>
                  <h4 className="text-lg font-medium text-text-primary mb-3">Small</h4>
                  <Snippet size="sm">npm start</Snippet>
                </div>
                
                <div>
                  <h4 className="text-lg font-medium text-text-primary mb-3">Medium (Default)</h4>
                  <Snippet size="md">npm run build</Snippet>
                </div>
                
                <div>
                  <h4 className="text-lg font-medium text-text-primary mb-3">Large</h4>
                  <Snippet size="lg">npm run dev -- --port 3000</Snippet>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        {/* Color Variations */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Color Variations</h3>
          <Card>
            <CardBody>
              <div className="space-y-6">
                <div>
                  <h4 className="text-lg font-medium text-text-primary mb-3">Default</h4>
                  <Snippet color="default">git status</Snippet>
                </div>
                
                <div>
                  <h4 className="text-lg font-medium text-text-primary mb-3">Primary</h4>
                  <Snippet color="primary">git commit -m "feat: add new feature"</Snippet>
                </div>
                
                <div>
                  <h4 className="text-lg font-medium text-text-primary mb-3">Secondary</h4>
                  <Snippet color="secondary">git branch feature/new-component</Snippet>
                </div>
                
                <div>
                  <h4 className="text-lg font-medium text-text-primary mb-3">Success</h4>
                  <Snippet color="success">npm test -- --passWithNoTests</Snippet>
                </div>
                
                <div>
                  <h4 className="text-lg font-medium text-text-primary mb-3">Warning</h4>
                  <Snippet color="warning">rm -rf node_modules</Snippet>
                </div>
                
                <div>
                  <h4 className="text-lg font-medium text-text-primary mb-3">Danger</h4>
                  <Snippet color="danger">sudo rm -rf /</Snippet>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        {/* Variant Styles */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Variant Styles</h3>
          <Card>
            <CardBody>
              <div className="space-y-6">
                <div>
                  <h4 className="text-lg font-medium text-text-primary mb-3">Flat (Default)</h4>
                  <Snippet variant="flat">docker build -t myapp .</Snippet>
                </div>
                
                <div>
                  <h4 className="text-lg font-medium text-text-primary mb-3">Bordered</h4>
                  <Snippet variant="bordered" color="primary">docker run -p 3000:3000 myapp</Snippet>
                </div>
                
                <div>
                  <h4 className="text-lg font-medium text-text-primary mb-3">Shadow</h4>
                  <Snippet variant="shadow" color="secondary">kubectl apply -f deployment.yaml</Snippet>
                </div>
                
                <div>
                  <h4 className="text-lg font-medium text-text-primary mb-3">Solid</h4>
                  <Snippet variant="solid" color="success">helm install myapp ./chart</Snippet>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        {/* Custom Symbols */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Custom Symbols</h3>
          <Card>
            <CardBody>
              <div className="space-y-6">
                <div>
                  <h4 className="text-lg font-medium text-text-primary mb-3">Default ($)</h4>
                  <Snippet>echo "Default symbol"</Snippet>
                </div>
                
                <div>
                  <h4 className="text-lg font-medium text-text-primary mb-3">Hash (#)</h4>
                  <Snippet symbol="#">echo "Hash symbol for comments"</Snippet>
                </div>
                
                <div>
                  <h4 className="text-lg font-medium text-text-primary mb-3">Greater Than (&gt;)</h4>
                  <Snippet symbol=">">echo "PowerShell style"</Snippet>
                </div>
                
                <div>
                  <h4 className="text-lg font-medium text-text-primary mb-3">Custom (→)</h4>
                  <Snippet symbol="→">echo "Custom arrow symbol"</Snippet>
                </div>
                
                <div>
                  <h4 className="text-lg font-medium text-text-primary mb-3">No Symbol</h4>
                  <Snippet symbol="">echo "No symbol prefix"</Snippet>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        {/* Copy Functionality */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Copy Functionality</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            
            <Card>
              <CardHeader>
                <h4 className="text-lg font-semibold text-text-primary">With Copy Button (Default)</h4>
              </CardHeader>
              <CardBody>
                <div className="space-y-4">
                  <Snippet color="primary">
                    git clone https://github.com/user/repo.git
                  </Snippet>
                  <p className="text-sm text-text-secondary">
                    Includes copy-to-clipboard functionality with visual feedback
                  </p>
                </div>
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                <h4 className="text-lg font-semibold text-text-primary">Without Copy Button</h4>
              </CardHeader>
              <CardBody>
                <div className="space-y-4">
                  <Snippet color="secondary" hideCopyButton>
                    # This is a read-only comment
                  </Snippet>
                  <p className="text-sm text-text-secondary">
                    Use for display-only code or comments that don't need copying
                  </p>
                </div>
              </CardBody>
            </Card>
          </div>
        </div>

        {/* Trading Context Examples */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Trading Journal Context</h3>
          <div className="space-y-6">
            
            {/* API Keys and Configuration */}
            <Card>
              <CardHeader>
                <h4 className="text-lg font-semibold text-text-primary">API Keys & Configuration</h4>
              </CardHeader>
              <CardBody>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-text-secondary mb-2">Broker API Configuration:</p>
                    <Snippet color="primary" size="sm">
                      ALPACA_API_KEY=your_api_key_here
                    </Snippet>
                  </div>
                  
                  <div>
                    <p className="text-sm text-text-secondary mb-2">Trading Bot Command:</p>
                    <Snippet color="success" variant="bordered">
                      python trading_bot.py --strategy momentum --risk 0.02
                    </Snippet>
                  </div>
                  
                  <div>
                    <p className="text-sm text-text-secondary mb-2">Database Connection:</p>
                    <Snippet color="secondary" size="sm">
                      postgresql://user:pass@localhost:5432/trading_db
                    </Snippet>
                  </div>
                </div>
              </CardBody>
            </Card>

            {/* Trading Calculations */}
            <Card>
              <CardHeader>
                <h4 className="text-lg font-semibold text-text-primary">Trading Calculations</h4>
              </CardHeader>
              <CardBody>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-text-secondary mb-2">Position Size Calculation:</p>
                    <Snippet color="primary" size="lg">
                      positionSize = riskAmount / Math.abs(entryPrice - stopLoss)
                    </Snippet>
                  </div>
                  
                  <div>
                    <p className="text-sm text-text-secondary mb-2">Risk Management Formula:</p>
                    <Snippet color="warning" variant="shadow">
                      maxRisk = accountBalance * 0.02; // 2% max risk per trade
                    </Snippet>
                  </div>
                  
                  <div>
                    <p className="text-sm text-text-secondary mb-2">P&L Calculation:</p>
                    <Snippet color="success">
                      pnl = (exitPrice - entryPrice) * quantity - commission
                    </Snippet>
                  </div>
                </div>
              </CardBody>
            </Card>

            {/* Trading Commands */}
            <Card>
              <CardHeader>
                <h4 className="text-lg font-semibold text-text-primary">Common Trading Commands</h4>
              </CardHeader>
              <CardBody>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-text-secondary mb-2">Start Trading Session:</p>
                    <Snippet color="primary" symbol="#">
                      npm run trading:start -- --paper-trading
                    </Snippet>
                  </div>
                  
                  <div>
                    <p className="text-sm text-text-secondary mb-2">Export Trade Data:</p>
                    <Snippet color="secondary">
                      python export_trades.py --from 2024-01-01 --format csv
                    </Snippet>
                  </div>
                  
                  <div>
                    <p className="text-sm text-text-secondary mb-2">Backup Trading Database:</p>
                    <Snippet color="warning" variant="bordered">
                      pg_dump trading_db &gt; backup_$(date +%Y%m%d).sql
                    </Snippet>
                  </div>
                </div>
              </CardBody>
            </Card>
          </div>
        </div>

        {/* Multiline Examples */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Multiline Code Examples</h3>
          <Card>
            <CardBody>
              <div className="space-y-6">
                <div>
                  <h4 className="text-lg font-medium text-text-primary mb-3">Trading Strategy Function</h4>
                  <Snippet color="primary" size="lg" variant="bordered">
{`// Calculate position size with risk management
const calculatePositionSize = (
  accountBalance,
  riskPercentage,
  entryPrice,
  stopLoss
) => {
  const riskAmount = accountBalance * (riskPercentage / 100);
  const priceRisk = Math.abs(entryPrice - stopLoss);
  return riskAmount / priceRisk;
};`}
                  </Snippet>
                </div>
                
                <div>
                  <h4 className="text-lg font-medium text-text-primary mb-3">Docker Setup for Trading Bot</h4>
                  <Snippet color="secondary" size="lg" variant="shadow" symbol="#">
{`# Build and run trading bot container
docker build -t trading-bot .
docker run -d \\
  --name my-trading-bot \\
  -e API_KEY=your_key \\
  -e PAPER_TRADING=true \\
  trading-bot`}
                  </Snippet>
                </div>
                
                <div>
                  <h4 className="text-lg font-medium text-text-primary mb-3">SQL Query for Trade Analysis</h4>
                  <Snippet color="success" size="lg" hideCopyButton>
{`SELECT 
  symbol,
  COUNT(*) as trade_count,
  AVG(pnl) as avg_pnl,
  SUM(CASE WHEN pnl > 0 THEN 1 ELSE 0 END) / COUNT(*) as win_rate
FROM trades 
WHERE created_at >= '2024-01-01'
GROUP BY symbol
ORDER BY avg_pnl DESC;`}
                  </Snippet>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        {/* Advanced Combinations */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Advanced Combinations</h3>
          <Card>
            <CardBody>
              <div className="space-y-6">
                <div>
                  <h4 className="text-lg font-medium text-text-primary mb-3">Mixed Styles & Features</h4>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <Snippet 
                      color="primary" 
                      variant="shadow" 
                      size="sm" 
                      symbol="→"
                    >
                      curl -X GET api.trading.com/positions
                    </Snippet>
                    
                    <Snippet 
                      color="danger" 
                      variant="bordered" 
                      size="md" 
                      hideCopyButton
                      symbol="⚠️"
                    >
                      DANGER: This will delete all data!
                    </Snippet>
                    
                    <Snippet 
                      color="success" 
                      variant="solid" 
                      size="lg" 
                      className="font-mono"
                    >
                      ✅ Trade executed successfully
                    </Snippet>
                    
                    <Snippet 
                      color="warning" 
                      variant="flat" 
                      symbol=""
                      className="italic"
                    >
                      // Note: Check margin requirements
                    </Snippet>
                  </div>
                </div>
                
                <div className="p-4 bg-default-100/50 dark:bg-default-800/50 rounded-lg">
                  <p className="text-sm text-text-secondary">
                    <strong>Pro Tip:</strong> Use different colors and variants to categorize your code snippets by context - 
                    primary for commands, success for confirmations, warning for cautions, and danger for destructive operations.
                  </p>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

      </div>
    </PlaygroundPageLayout>
  );
};